/**
 * "Preparing your rows" — shown over Review rules while the confirmed mapping
 * is being applied (normalised.parquet is written in the background after
 * Confirm mapping). Rules can only be previewed and run on those rows, so the
 * page waits behind this until the file is NORMALISED, then the dialog closes
 * by itself.
 *
 * Not closable while it runs; if the run fails it says so and offers a way out.
 * `runId` comes from Confirm mapping; opened later without it, the dialog still
 * waits on the file's status, just without a percentage.
 */

import { Link } from 'react-router-dom';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	LinearProgress,
	Typography,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useProcessingRunQuery } from '../../queries/intake';

export function PreparingRowsDialog({
	open,
	runId,
	fileName,
	fileHref,
	filesHref,
}: {
	open: boolean;
	runId: string | null;
	fileName: string;
	fileHref: string;
	filesHref: string;
}) {
	const run = useProcessingRunQuery(runId, open);
	const failed = run.data?.status === 'FAILED';
	const progress = run.data?.progress ?? null;

	return (
		<Dialog
			open={open}
			/* No close on backdrop or Escape: the page behind cannot be used yet. */
			onClose={() => undefined}
			disableEscapeKeyDown
			maxWidth="xs"
			fullWidth
			aria-labelledby="preparing-rows-title"
		>
			<DialogTitle
				id="preparing-rows-title"
				sx={{ fontSize: 'var(--fs-section)', fontWeight: 600, pb: 1 }}
			>
				{failed
					? 'The rows could not be prepared'
					: 'Preparing your rows'}
			</DialogTitle>
			<DialogContent
				sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: 'var(--s3)',
				}}
			>
				<Typography
					noWrap
					sx={{ fontSize: 'var(--fs-small)', color: 'var(--ink-3)' }}
				>
					{fileName}
				</Typography>

				{failed ? (
					<Box
						sx={{
							display: 'flex',
							gap: 'var(--s2)',
							p: 'var(--s3)',
							borderRadius: 'var(--r-lg)',
							bgcolor: 'var(--danger-surface)',
							color: 'var(--danger-ink)',
						}}
					>
						<ErrorOutlineIcon fontSize="small" />
						<Typography sx={{ fontSize: 'var(--fs-body)' }}>
							{run.data?.errorMessage ??
								'The conversion stopped without a reason.'}
						</Typography>
					</Box>
				) : (
					<>
						<Typography
							sx={{
								fontSize: 'var(--fs-body)',
								color: 'var(--ink-2)',
							}}
						>
							The mapping is confirmed. Its columns are being
							applied to every row — the rules can be reviewed and
							run as soon as this finishes.
						</Typography>
						<LinearProgress
							variant={
								progress !== null && progress > 0
									? 'determinate'
									: 'indeterminate'
							}
							value={progress ?? 0}
							sx={{ height: 6, borderRadius: 'var(--r-full)' }}
						/>
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'space-between',
								gap: 'var(--s2)',
								fontSize: 'var(--fs-small)',
								color: 'var(--ink-3)',
							}}
						>
							<Typography
								noWrap
								component="span"
								sx={{ fontSize: 'inherit' }}
							>
								{run.data?.progressNote ?? 'Converting values…'}
							</Typography>
							{progress !== null && progress > 0 && (
								<Typography
									component="span"
									sx={{ fontSize: 'inherit' }}
								>
									{Math.round(progress)}%
								</Typography>
							)}
						</Box>
					</>
				)}
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button component={Link} to={filesHref} variant="text">
					Back to files
				</Button>
				{/* A way out while waiting too: a conversion that never started
            leaves the file here, and the file page shows its runs. */}
				<Button
					component={Link}
					to={fileHref}
					variant={failed ? 'contained' : 'outlined'}
				>
					Go to file
				</Button>
			</DialogActions>
		</Dialog>
	);
}
